// HomeBannerItem.jsx
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import DecryptionWrapper from '../container/DecryptionWrapper.js';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import ResponsiveInfoButton from '../component/ResponsiveInfoButton.jsx';
import StatefulImage from '../component/StatefulImage.jsx';
import { color } from '../style/variables.js';

import BannerPlaceholderResource from '../../img/placeholder-banner.svg';

import { ButtonId } from '../resource/mixpanel.js';
import { EMPTY_OBJECT, NULL_FUNCTION } from '../resource/defaults.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

export const HomeBannerItem = ({
  bannerLink = '',
  category = '',
  bannerData = EMPTY_OBJECT,
  isIntersecting = false,
  meId = '',
  index = 0,
  fetchBanner = NULL_FUNCTION,
  toggleModal = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    if (isIntersecting) {
      fetchBanner({ category, url: bannerLink });
    }
  }, [isIntersecting, category, bannerLink, meId, fetchBanner]);
  if (!bannerLink || !bannerData?.src) {
    return (
      <StatefulImageWrapper>
        <PlaceholderWrapper data-key="target">
          <img
            src={BannerPlaceholderResource}
            alt="banner placeholder"
            width={300}
            height={136}
          />
        </PlaceholderWrapper>
      </StatefulImageWrapper>
    );
  }
  const replacedLink =
    bannerData.link == null
      ? ''
      : decodeURI(bannerData.link).replace(/{USER_ID}/g, meId);
  const trackingButtonId =
    category === 'pseudo_video_banner'
      ? ButtonId.Home.ButtonVideoBanners
      : ButtonId.Home.ButtonHomeBanners;
  const trackingPayload = {
    url: bannerData.link,
    'si.url': bannerData.src,
    'banner.order': index + 1,
  };

  const bannerContent = (
    <StatefulImageWrapper>
      <StatefulImage>
        <PlaceholderWrapper data-key="loading">
          <img
            src={BannerPlaceholderResource}
            alt="banner placeholder"
            width={300}
            height={136}
          />
        </PlaceholderWrapper>
        <PlaceholderWrapper data-key="error">
          <img
            src={BannerPlaceholderResource}
            alt="banner placeholder"
            width={300}
            height={136}
          />
        </PlaceholderWrapper>
        <DecryptionWrapper resourceUrl={bannerData.src} data-key="target">
          <BannerImage src={bannerData.src} alt="banner" />
        </DecryptionWrapper>
      </StatefulImage>
    </StatefulImageWrapper>
  );

  if (bannerData.link == null || !replacedLink) {
    return (
      <BannerWrapper
        data-element_id={trackingButtonId}
        data-tracking_payload={trackingPayload}
      >
        {bannerContent}
      </BannerWrapper>
    );
  }

  if (bannerData.reactLink) {
    return (
      <BannerWrapper
        as={Link}
        to={replacedLink}
        data-element_id={trackingButtonId}
        data-tracking_payload={trackingPayload}
      >
        {bannerContent}
      </BannerWrapper>
    );
  }

  return (
    <InteractiveBanner
      articleUrl={replacedLink}
      modalId="IframeModal"
      modalHeaderTitle={
        bannerData.title ||
        t('home_banner', {
          ns: TranslationNamespace.GENERAL,
        })
      }
      toggleModal={toggleModal}
      trackingModalId={trackingButtonId}
      trackingPayload={trackingPayload}
    >
      {bannerContent}
    </InteractiveBanner>
  );
};

HomeBannerItem.propTypes = {
  bannerLink: PropTypes.string,
  category: PropTypes.string,
  meId: PropTypes.string,
  bannerData: PropTypes.object,
  isIntersecting: PropTypes.bool,
  index: PropTypes.number,
  fetchBanner: PropTypes.func,
  toggleModal: PropTypes.func,
};

const BannerWrapper = styled.div``;

const InteractiveBanner = styled(ResponsiveInfoButton)``;

const BannerImage = styled.img`
  border-radius: 8px;
  border: solid 1px ${color.grey[700]};
  width: 300px;
  height: 136px;
`;

const PlaceholderWrapper = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  border: solid 1px ${color.grey[700]};
`;

const StatefulImageWrapper = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
`;

export default HomeBannerItem;
